import { Dispatch } from 'redux';

import * as Actions from '../actions/profile';
import AbstractScenario from './AbstractScenario';

/** ユーザ情報登録シナリオ用パラメータ */
export interface ISaveUserProfileParam {
	userProfile: Actions.IProfile;
	storedProfile: Actions.IProfile;
}

/**
 * ユーザ情報シナリオファクトリ
 */
export default class ProfileScenarioFactory {
	/**
	 * ユーザ情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchProfile(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<{}> {
			protected process(arg: {}) {
				const tasks = [];
				tasks.push(
					// 国マスタ取得タスク
					this.read('tsq_common_GenericDataModel', {
						_tsqGenericDataType: 'mm_user_countryMstListCollection',
					}).then(data => {
						if (data.countryMstList) {
							const value: { [code: string]: string } = {};
							for (const countryMst of data.countryMstList) {
								value[countryMst.code] = countryMst.value;
							}
							this.dispatch(Actions.setCountryMaster({ value }));
						}
					})
				);
				tasks.push(
					// タイムゾーンマスタ取得タスク
					this.read('tsq_common_GenericDataModel', {
						_tsqGenericDataType: 'mm_user_timezoneListCollection',
					}).then(data => {
						if (data.timezoneMstList) {
							const value: { [code: string]: string } = {};
							for (const timezoneMst of data.timezoneMstList) {
								value[timezoneMst.code] = timezoneMst.value;
							}
							this.dispatch(Actions.setTimezoneMaster({ value }));
						}
					})
				);
				tasks.push(
					// 言語マスタ取得タスク
					this.read('tsq_common_GenericDataModel', {
						_tsqGenericDataType: 'mm_user_langMstListCollection',
					}).then(data => {
						if (data.langMstList) {
							const value: { [code: string]: string } = {};
							for (const langMst of data.langMstList) {
								value[langMst.code] = langMst.value;
							}
							this.dispatch(Actions.setLangMaster({ value }));
						}
					})
				);

				// タスク並列実行
				return Promise.all(tasks)
					.then(() => {
						// ユーザ情報取得
						return this.read('per_user_UserDetailBlockModel').then(
							data => {
								const profile: Actions.IProfile = data;
								this.dispatch(
									Actions.setStoredProfile({ value: profile })
								);
								this.dispatch(
									Actions.setEditedProfile({ value: profile })
								);
								return profile;
							}
						);
					})
					.then((profile: Actions.IProfile) => {
						// 事業所マスタ取得タスク
						this.read('tsq_common_GenericDataModel', {
							_tsqGenericDataType:
								'mm_user_generalCodeMstByOrgIdCollection',
							itemType: 'PC02',
							orgId: profile.primaryOrgId,
						}).then(data => {
							if (data.generalCodeList) {
								const value: { [code: string]: string } = {};
								for (const officeMst of data.generalCodeList) {
									value[officeMst.code] = officeMst.value;
								}
								this.dispatch(
									Actions.setOfficeMaster({ value })
								);
							}
						});

						// 従業員区分マスタ取得タスク
						this.read('tsq_common_GenericDataModel', {
							_tsqGenericDataType:
								'mm_user_generalCodeMstByOrgIdCollection',
							itemType: 'PC04',
							orgId: profile.primaryOrgId,
						}).then(data => {
							if (data.generalCodeList) {
								const value: { [code: string]: string } = {};
								for (const employeeCategoryMst of data.generalCodeList) {
									value[employeeCategoryMst.code] =
										employeeCategoryMst.value;
								}
								this.dispatch(
									Actions.setEmployeeCategoryMaster({ value })
								);
							}
						});

						// 職種マスタ取得タスク
						this.read('tsq_common_GenericDataModel', {
							_tsqGenericDataType:
								'mm_user_generalCodeMstByOrgIdCollection',
							itemType: 'PC03',
							orgId: profile.primaryOrgId,
						}).then(data => {
							if (data.generalCodeList) {
								const value: { [code: string]: string } = {};
								for (const occupationMst of data.generalCodeList) {
									value[occupationMst.code] =
										occupationMst.value;
								}
								this.dispatch(
									Actions.setOccupationMaster({ value })
								);
							}
						});
					});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * ユーザ情報登録 シナリオ作成
	 * @static
	 * @param {Dispatch} dispatch
	 * @param {string} token
	 * @returns
	 * @memberof ProfileScenarioFactory
	 */
	public static createSaveUserProfile(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			ISaveUserProfileParam
		> {
			protected process(arg: ISaveUserProfileParam) {
				const params = JSON.parse(JSON.stringify(arg.userProfile));
				return this.update('per_user_UserDetailBlockModel', params)
					.then(data => {
						// キー情報が変更されたかどうかをチェック
						const checkChangeProperties = [
							'familyNameJa',
							'firstNameJa',
							'lang',
							'timeZone',
						];
						let isChanged = false;
						checkChangeProperties.map((key: string) => {
							const before = arg.storedProfile[key];
							const after = arg.userProfile[key];
							if (before !== after) {
								isChanged = true;
							}
						});

						if (isChanged) {
							this.dispatch(
								Actions.setSaveProfileCompleteFlg({
									needLogoutFlg: true,
									saveCompleteFlg: true,
								})
							);
						} else {
							this.dispatch(
								Actions.setSaveProfileCompleteFlg({
									needLogoutFlg: false,
									saveCompleteFlg: true,
								})
							);
						}

						const profile: Actions.IProfile = data;
						profile.password = '';
						profile.rePassword = '';
						this.dispatch(
							Actions.setStoredProfile({ value: profile })
						);
						this.dispatch(
							Actions.setEditedProfile({ value: profile })
						);
						this.dispatch(
							Actions.setSaveLoadingFlg({
								loading: false,
							})
						);
					})
					.catch(() => {
						this.dispatch(
							Actions.setSaveLoadingFlg({
								loading: false,
							})
						);
					});
			}
		}(dispatch, token);
		return scenario;
	}
}



// WEBPACK FOOTER //
// ./src/scenario/ProfileScenarioFactory.ts